import * as React from "react";
import { Button as AButton } from "antd-mobile";
import "../Button/index.less";
import "./index.less";

type AButtonProps = React.ComponentProps<typeof AButton>;

export type LoadingButtonProps = Omit<
  AButtonProps,
  "loading" | "loadingIcon" | "loadingText" | "shape" | "size"
> & {
  secondary?: boolean;
  outline?: boolean;
  icon?: React.ReactNode;
  subTitle?: string;
  loading?: boolean | "auto";
};

type PressPhase = "idle" | "pressed" | "releasing";

const GoColorToken = {
  $go_color_grey_5: "#F5F5F7",
  $go_color_grey_6: "#FFFFFF",
  $go_color_grey_1: "#0B0B0D",
  $brand_color_primary: "#0A7BFF",
  $brand_color_primary_disabled: "#8BC0F9",
};

const Theme: Partial<
  Record<
    | "--border-radius"
    | "--text-color"
    | "--background-color"
    | "--border-width"
    | "--border-style"
    | "--border-color"
    | "--go-disabled-background-color"
    | "--go-disabled-text-color",
    string
  >
> = {
  "--border-radius": "999px",
  "--text-color": GoColorToken.$go_color_grey_6,
  "--background-color": GoColorToken.$brand_color_primary,
  "--go-disabled-background-color": GoColorToken.$brand_color_primary_disabled,
  "--go-disabled-text-color": GoColorToken.$go_color_grey_6,
};

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    (typeof value === "object" || typeof value === "function") &&
    value !== null &&
    "then" in value &&
    typeof (value as PromiseLike<unknown>).then === "function"
  );
}

export const LoadingButton = React.forwardRef<React.ElementRef<typeof AButton>, LoadingButtonProps>(
  function LoadingButton(props, ref) {
    const {
      secondary,
      outline,
      icon,
      subTitle,
      loading = false,
      disabled,
      className,
      style,
      children,
      onClick,
      onMouseDown,
      onMouseUp,
      onTouchStart,
      onTouchEnd,
      ...rest
    } = props;
    const [autoLoading, setAutoLoading] = React.useState(false);
    const [pressPhase, setPressPhase] = React.useState<PressPhase>("idle");
    const mountedRef = React.useRef(true);
    const isLoading = loading === "auto" ? autoLoading : loading;
    const isDisabled = Boolean(disabled || isLoading);
    const isOutline = outline === true;
    const hasText = children !== undefined && children !== "";

    React.useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    React.useEffect(() => {
      if (isDisabled) {
        setPressPhase("idle");
      }
    }, [isDisabled]);

    const startPress = React.useCallback(() => {
      if (!isDisabled) {
        setPressPhase("pressed");
      }
    }, [isDisabled]);

    const releasePress = React.useCallback(() => {
      setPressPhase((phase) => (phase === "pressed" ? "releasing" : phase));
    }, []);

    const handleClick = React.useCallback<NonNullable<AButtonProps["onClick"]>>(
      (event) => {
        if (isDisabled || !onClick) {
          return undefined;
        }

        const result = onClick(event);
        if (loading !== "auto" || !isPromiseLike(result)) {
          return result;
        }

        setAutoLoading(true);
        return Promise.resolve(result).finally(() => {
          if (mountedRef.current) {
            setAutoLoading(false);
          }
        });
      },
      [isDisabled, loading, onClick],
    );

    const buttonStyle = {
      ...style,
      ...Theme,
    } as AButtonProps["style"];

    if (secondary) {
      buttonStyle!["--background-color"] = GoColorToken.$go_color_grey_5;
      buttonStyle!["--text-color"] = GoColorToken.$go_color_grey_1;
    }
    if (isOutline) {
      buttonStyle!["--background-color"] = "transparent";
      buttonStyle!["--text-color"] = "#0C0C0D";
      buttonStyle!["--border-width"] = "0.1rem";
      buttonStyle!["--border-style"] = "solid";
      buttonStyle!["--border-color"] = "#606066";
    }

    const buttonClassName = [
      "go-button",
      "go-button-middle",
      "go-button-fintech" + (isOutline ? "-outline" : secondary ? "-secondary" : ""),
      "go-loading-button",
      isLoading ? "go-loading-button-loading" : "",
      isDisabled ? "go-loading-button-disabled" : "",
      `go-loading-button-press-${pressPhase}`,
      !hasText ? "go-button-notext" : "",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    const label = hasText ? (
      <div className="go-button-text">
        <div className="go-button-title">
          {icon ? <div className="go-button-icon">{icon}</div> : null}
          {children}
        </div>
        {subTitle ? <div className="go-button-subtitle">{subTitle}</div> : null}
      </div>
    ) : (
      <div className="go-button-icon">{icon}</div>
    );

    return (
      <AButton
        ref={ref}
        {...rest}
        className={buttonClassName}
        disabled={isDisabled}
        loading={false}
        shape="rounded"
        size="middle"
        style={buttonStyle}
        aria-busy={isLoading || undefined}
        onClick={handleClick}
        onMouseDown={(event) => {
          if (isDisabled) return;
          onMouseDown?.(event);
          startPress();
        }}
        onMouseUp={(event) => {
          if (isDisabled) return;
          onMouseUp?.(event);
          releasePress();
        }}
        onTouchStart={(event) => {
          if (isDisabled) return;
          onTouchStart?.(event);
          startPress();
        }}
        onTouchEnd={(event) => {
          if (isDisabled) return;
          onTouchEnd?.(event);
          releasePress();
        }}
      >
        <div className="go-loading-button-content">
          <span
            className={`go-loading-button-press-layer go-loading-button-press-layer-${pressPhase}`}
            aria-hidden="true"
            onTransitionEnd={(event) => {
              if (pressPhase === "releasing" && event.propertyName === "opacity") {
                setPressPhase("idle");
              }
            }}
          />
          <div
            className={`go-loading-button-label${isLoading ? " go-loading-button-label-hidden" : ""}`}
          >
            {label}
          </div>
          <span
            className={`go-loading-button-loader${isLoading ? " go-loading-button-loader-visible" : ""}`}
            aria-hidden="true"
          >
            <svg
              className="go-loading-button-loader-icon"
              viewBox="109 169 275 104"
              focusable="false"
            >
              <path
                className="go-loading-button-loader-segment go-loading-button-loader-segment--lead-left"
                d="M 131.7 221.05 L 131.7 277 Q 131.7 352.8 207.5 352.8 Q 283.3 352.8 283.3 277 L 283.3 165.1 Q 283.3 89.3 207.5 89.3 Q 131.7 89.3 131.7 165.1 Z"
                pathLength="100"
              />
              <path
                className="go-loading-button-loader-segment go-loading-button-loader-segment--lead-right"
                d="M 131.5 221.05 L 131.5 277 Q 131.5 352.8 207.3 352.8 Q 283.1 352.8 283.1 277 L 283.1 165.1 Q 283.1 89.3 207.3 89.3 Q 131.5 89.3 131.5 165.1 Z"
                pathLength="100"
              />
              <path
                className="go-loading-button-loader-segment go-loading-button-loader-segment--follow-left"
                d="M 210.5 221.05 L 210.5 277 Q 210.5 352.8 286.3 352.8 Q 362.1 352.8 362.1 277 L 362.1 165.1 Q 362.1 89.3 286.3 89.3 Q 210.5 89.3 210.5 165.1 Z"
                pathLength="100"
              />
              <path
                className="go-loading-button-loader-segment go-loading-button-loader-segment--follow-right"
                d="M 210.8 221.05 L 210.8 277 Q 210.8 352.8 286.6 352.8 Q 362.4 352.8 362.4 277 L 362.4 165.1 Q 362.4 89.3 286.6 89.3 Q 210.8 89.3 210.8 165.1 Z"
                pathLength="100"
              />
            </svg>
          </span>
        </div>
      </AButton>
    );
  },
);
